import Router, { useRouter } from 'next/router'
import Head from 'next/head'
import Link from 'next/link'
import Script from 'next/script'
import NProgress from 'nprogress'
import ScrollTopButton from '../components/ScrollTopButton'
import { PrimeReactProvider } from 'primereact/api';
import '../scss/theme.scss'
import "primereact/resources/themes/bootstrap4-light-blue/theme.css";
import '../css/PrimeReactFixes.css';

import { StrictMode, useEffect } from 'react';
import { getI18n } from '../libs/utils/i18n';
import { I18nextProvider } from "react-i18next";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import { Inter } from 'next/font/google';
import { loadPixel } from '../libs/fpixel';

// Self-hosted at build time (no request to fonts.googleapis.com, preloaded,
// font-display: swap). The theme reads it through --font-inter.
const inter = Inter({ subsets: ['latin'], display: 'swap' });

const COOKIE_CONSENT_NAME = 'cookie_consent'

// Analytics start in "denied" mode (see _document.js) and are only
// enabled once the visitor accepts cookies.
const updateTrackingConsent = (granted) => {
    const state = granted ? 'granted' : 'denied'
    window.gtag?.('consent', 'update', {
        analytics_storage: state,
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
    })
    if (granted) {
        loadPixel()
    } else {
        window.fbq?.('consent', 'revoke')
    }
}

const Finder = ({ Component, pageProps }) => {
    const router = useRouter();
    const { locale } = router; // Get the current locale
    const i18n = getI18n(locale);
    const { t } = i18n;

    // Bind NProgress to Next Router events (Page loading animation)
    useEffect(() => {
        const start = () => NProgress.start()
        const done = () => NProgress.done()
        Router.events.on('routeChangeStart', start)
        Router.events.on('routeChangeComplete', done)
        Router.events.on('routeChangeError', done)
        return () => {
            Router.events.off('routeChangeStart', start)
            Router.events.off('routeChangeComplete', done)
            Router.events.off('routeChangeError', done)
        }
    }, [])

    // Re-apply a consent given on a previous visit
    useEffect(() => {
        if (getCookieConsentValue(COOKIE_CONSENT_NAME) === 'true') {
            updateTrackingConsent(true)
        }
    }, [])

    useEffect(() => {
        const direction = locale === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.dir = direction;
        document.documentElement.lang = locale;
    }, [locale]);

    return (
        <StrictMode>
            <style jsx global>{`
                :root { --font-inter: ${inter.style.fontFamily}; }
            `}</style>
            <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Facilykey</title>
                <meta name='author' content='Hisham HADRAOUI' />
                <link rel='apple-touch-icon' sizes='180x180' href='/favicon/apple-touch-icon.png' />
                <link rel='icon' type='image/png' sizes='32x32' href='/favicon/favicon-32x32.png' />
                <link rel='icon' type='image/png' sizes='16x16' href='/favicon/favicon-16x16.png' />
                <link rel='manifest' href='/favicon/site.webmanifest' />
                <link rel='mask-icon' color='#5bbad5' href='/favicon/safari-pinned-tab.svg' />
                <meta name='msapplication-TileColor' content='#766df4' />
                <meta name='theme-color' content='#ffffff' />
            </Head>

            {/* Analytics: queued in dataLayer by _document, script loaded once the page is idle */}
            <Script src='https://www.googletagmanager.com/gtag/js?id=G-18BCQKZQL5' strategy='lazyOnload' />

            <I18nextProvider i18n={i18n}>
                <PrimeReactProvider>
                    <CookieConsent
                        location="bottom"
                        buttonText={t('cookie_accept')}
                        declineButtonText={t('cookie_decline')}
                        cookieName={COOKIE_CONSENT_NAME}
                        style={{ background: "#023059", color: "#ffffff", paddingInlineEnd: "4.5rem" /* room for the scroll-to-top button */, zIndex: 1045 /* above fixed-bottom buttons (catalog filters) */, boxShadow: "0 -0.125rem 0.5rem rgba(27,36,45,0.12)" }}
                        buttonStyle={{ padding: "0.575rem 1.5rem", fontWeight: "600", borderRadius: "0.5rem", background: "#11C5D9", color: "#023059", fontSize: "0.875rem", border: "none" }}
                        declineButtonStyle={{ padding: "0.575rem 1.5rem", fontWeight: "600", borderRadius: "0.5rem", background: "transparent", color: "#ff8a80", fontSize: "0.875rem", border: "1px solid #ff8a80" }}
                        enableDeclineButton
                        // Accessible names must match the visible (translated) button text
                        ariaAcceptLabel={t('cookie_accept')}
                        ariaDeclineLabel={t('cookie_decline')}
                        onAccept={() => updateTrackingConsent(true)}
                        onDecline={() => updateTrackingConsent(false)}
                    >
                        {t('cookie_consent_text')}{' '}
                        <Link href='/cookies' style={{ color: '#ffffff', textDecoration: 'underline' }}>{t('En savoir plus')}</Link>
                    </CookieConsent>
                    <Component {...pageProps} />
                </PrimeReactProvider>
            </I18nextProvider>

            <ScrollTopButton
                showOffset={600}
                tooltip='Top'
                label={t('Retour en haut de page')}
            />
        </StrictMode >
    )
}

export default Finder;
